import { RefObject, useEffect, useMemo, useRef, useState } from "react"
import { BaseMapRef } from "./BaseMap"
import { DrivingRoute } from "./layers/RouteLayer"
import { isActiveTech, useTechs } from "../../core/Global"
import { onTechLive } from "../../core/TechLive"
import { HereGeocodePosition } from "../../models/MapModels"
import Technician from "../../modules/technicians/models/technician"
import { isRealPosition, milesBetween, resolveTechPosition } from "./mapUtils"

/** How many of the nearest technicians the first view brings in around the job. */
const NEAREST_IN_FIRST_VIEW = 3
/** Nobody further than this is pulled into the first view, however few are nearer. */
const FIRST_VIEW_MAX_MILES = 60

interface Options {
    mapRef: RefObject<BaseMapRef>
    /** The Leaflet map exists (BaseMap's onReady has fired). Nothing is framed before it. */
    mapReady: boolean
    jobPosition?: HereGeocodePosition
    /** The technicians who may take the job. Anybody else on the map is stepped back. */
    techs?: Technician[]
    selectedTech?: Technician
}

/**
 * What a map for giving a job a technician needs, whichever screen it is on: everybody to draw
 * and who of them is stepped back, where the picked technician is, and the views -- the job with
 * the pick or with the nearest few, everyone, the route once it is known. The first view is set
 * once the job and the list are known, again for every new job address, and a new pick brings the
 * job and the picked technician into view.
 */
export function useJobTechsView(opts: Options) {

    const { mapRef, mapReady, jobPosition, techs, selectedTech } = opts
    const jobKey = isRealPosition(jobPosition) ? `${jobPosition.lat},${jobPosition.lng}` : ""
    const bank = useTechs()

    // A live point moves a pin by itself; the route to the picked technician starts from here,
    // so it has to hear about it too.
    const [liveTick, liveTickSet] = useState(0)
    useEffect(() => onTechLive(techId => {
        if (techId === selectedTech?.id) liveTickSet(t => t + 1)
    }), [selectedTech?.id])

    // Everybody who can still take work, with the list's fresher figures where the list has them.
    // Somebody on the list but not in the bank (just picked off the map) is kept as well.
    const allTechs = useMemo(() => {
        const listed = new Map((techs ?? []).map(t => [t.id, t]))
        const merged = bank.filter(isActiveTech).map(t => listed.get(t.id) ?? t)
        ;(techs ?? []).forEach(t => { if (!merged.some(m => m.id === t.id)) merged.push(t) })
        return merged
    }, [bank, techs])

    const dimmed = useMemo(() => {
        if (!techs) return undefined
        const listed = new Set(techs.map(t => t.id))
        return new Set(allTechs.filter(t => !listed.has(t.id)).map(t => t.id as string))
    }, [allTechs, techs])

    const selectedPos = useMemo(() => {
        void liveTick
        const tech = selectedTech && (allTechs.find(t => t.id === selectedTech.id) ?? selectedTech)
        return tech ? resolveTechPosition(tech)?.position : undefined
    }, [selectedTech, allTechs, liveTick])

    // The views below are called from Leaflet handlers and timers; through this they read the
    // latest render, not the one they were made in.
    const latest = useRef({ jobPosition, techs, selectedPos, selectedTech })
    latest.current = { jobPosition, techs, selectedPos, selectedTech }

    const nearestPositions = () => {
        const job = latest.current.jobPosition
        if (!isRealPosition(job)) return []
        return (latest.current.techs ?? [])
            .map(t => resolveTechPosition(t)?.position)
            .filter(isRealPosition)
            .map(p => ({ p, miles: milesBetween(p, job) ?? Infinity }))
            .filter(x => x.miles <= FIRST_VIEW_MAX_MILES)
            .sort((a, b) => a.miles - b.miles)
            .slice(0, NEAREST_IN_FIRST_VIEW)
            .map(x => x.p)
    }

    /** The job with the picked technician, or with the nearest few when nobody is picked. */
    const showJobAndPick = () => {
        const { jobPosition: job, selectedPos: pick } = latest.current
        if (pick) mapRef.current?.fitTo([job, pick], { padding: 70, maxZoom: 14 })
        else mapRef.current?.fitTo([job, ...nearestPositions()], { padding: 60, maxZoom: 13 })
    }

    /** The job and everybody on the list. */
    const showEveryone = () => {
        const { jobPosition: job, techs: list } = latest.current
        mapRef.current?.fitTo([job, ...(list ?? []).map(t => resolveTechPosition(t)?.position)], { padding: 40 })
    }

    /** The route, once it is back, is what the view should hold: it may bend well outside the straight line. */
    const fitRoute = (route?: DrivingRoute) => {
        if (route && latest.current.selectedTech?.id) mapRef.current?.fitTo([latest.current.jobPosition, ...route.points], { padding: 70, maxZoom: 14 })
    }

    // A form that opens with the address already in it (a job made from a chat message) has its
    // job and list before the map exists; framing then would do nothing and never be tried again.
    const framedFor = useRef("")
    useEffect(() => {
        if (!mapReady || !jobKey || !techs || framedFor.current === jobKey) return
        framedFor.current = jobKey
        showJobAndPick()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady, jobKey, techs])

    useEffect(() => {
        if (framedFor.current && selectedTech?.id) showJobAndPick()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTech?.id])

    return { allTechs, dimmed, selectedPos, showJobAndPick, showEveryone, fitRoute }
}
